import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-theme focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        "outline-shadow":
          "border border-input/50 bg-background hover:bg-accent hover:text-accent-foreground hover:border-input/80 transition-all duration-300",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        gradient: "bg-gradient-primary text-primary-foreground hover:brightness-110 transition-all duration-300",
        "gradient-soft": "bg-gradient-to-br from-muted/80 to-accent/60 text-foreground border border-input/50 hover:from-muted hover:to-accent/80 hover:border-input/80 transition-all duration-300",
        "nanny-primary": "bg-nanny-primary text-nanny-primary-foreground hover:bg-nanny-primary/90 shadow-sm",
        "nanny-secondary": "bg-nanny-secondary text-nanny-secondary-foreground hover:bg-nanny-secondary/90 shadow-sm",
        white: "bg-primary-foreground text-primary hover:bg-primary-foreground/90 active:scale-[0.98] transition-all",
        black: "bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.98] transition-all",
        grey: "bg-muted text-foreground hover:bg-muted/90 active:scale-[0.98] transition-all",
        "dark-grey": "bg-muted-foreground/20 text-foreground hover:bg-muted-foreground/30 active:scale-[0.98] transition-all",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        // Mobile-optimized sizes
        mobile: "h-12 px-6 py-3 text-base", // 48px touch target
        "mobile-sm": "h-11 px-4 py-2.5", // 44px touch target
        "mobile-lg": "h-14 px-8 py-4 text-lg", // Large mobile buttons
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
